import { Popover } from "@headlessui/react";
import { useState } from "react";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show,
  TextArea
} from "shared/components";
import { DotsHorizontal, Pencil02 } from "untitledui-js-base";
import {
  ActivityItem,
  ApplicationActivity,
  ApplicationNote,
  DeleteNoteMutation,
  EditNoteMutation
} from "./types";

import moment from "moment";
import {
  applicationDecisionStatus,
  combinedRoles
} from "shared/app/ActivityLog/constants";
import { ApplicantType } from "shared/types";
import { ApplicantRoleLabel } from "shared/utils";
import { ApplicantRole } from "../../../../../applicant/common/components/ApplicantRole";
import { getActivityMessage, getAuthorText } from "./helpers";
import { RoleTitles } from "views/settings-users/constants";
import { getFullName } from "shared/utils/get-full-name";

const buttonClassName =
  "w-full flex px-4 py-2.5 !text-base !text-content-1000 !font-medium hover:bg-alpha-40";

export const ActivityNote = ({
  deleteNoteMeta,
  editNoteMeta,
  activityItem
}: {
  activityItem: ActivityItem;
  deleteNoteMeta: DeleteNoteMutation;
  editNoteMeta: EditNoteMutation;
}) => {
  const [editMode, setEditMode] = useState<boolean>(false);
  const [newMessage, setNewMessage] = useState<string>(
    typeof activityItem.details === "string" ? activityItem.details : ""
  );

  const message = getActivityMessage(activityItem);

  return (
    <div className="animate-appear w-full ">
      <div className="border rounded-lg border-alpha-100 p-6 w-full bg-white">
        <div className="flex justify-between relative mb-2">
          <Show
            if={
              activityItem.activity_type ===
              "complete_request_retry_identity_verification"
            }
          >
            <div className="space-y-2">
              <p className="text-base font-bold text-content-1000">
                Identity verification retry was completed{" "}
                <span className="font-normal">
                  by {activityItem.author?.name} (
                  {activityItem.author?.author_role
                    ? combinedRoles[activityItem.author.author_role]
                    : "-"}
                  )
                </span>
              </p>
            </div>
          </Show>

          <Show
            if={
              activityItem.activity_type ===
              "cancel_request_retry_identity_verification"
            }
          >
            <div className="space-y-2">
              <p className="text-base font-bold text-content-1000">
                Identity verification retry request was canceled{" "}
                <span className="font-normal">
                  by {activityItem.author?.name}
                </span>
              </p>
              {activityItem.details &&
              typeof activityItem.details === "object" &&
              activityItem.details.contact ? (
                <p className="text-sm font-normal text-content-400">
                  For{" "}
                  {getFullName({
                    firstName: activityItem.details.contact.first_name,
                    lastName: activityItem.details.contact.last_name,
                    middleName: activityItem.details.contact.middle_name,
                    short: true
                  })}{" "}
                  (
                  {activityItem.details.contact.role
                    ? combinedRoles[activityItem.details.contact.role]
                    : "-"}
                  )
                </p>
              ) : null}
            </div>
          </Show>

          <Show
            if={
              activityItem.activity_type ===
              "request_retry_identity_verification"
            }
          >
            <div className="space-y-2">
              <p className="text-base font-bold text-content-1000">
                Identity verification request was re-sent{" "}
                <span className="font-normal">
                  by {activityItem.author?.name}
                </span>
              </p>
              {activityItem.details &&
              typeof activityItem.details === "object" &&
              activityItem.details.contact ? (
                <p className="text-sm font-normal text-content-400">
                  For{" "}
                  {getFullName({
                    firstName: activityItem.details.contact.first_name,
                    lastName: activityItem.details.contact.last_name,
                    middleName: activityItem.details.contact.middle_name,
                    short: true
                  })}{" "}
                  (
                  {activityItem.details.contact.role
                    ? combinedRoles[activityItem.details.contact.role]
                    : "-"}
                  )
                </p>
              ) : null}
            </div>
          </Show>

          <Show
            if={
              activityItem.activity_type === "canadian_criminal_report_invited"
            }
          >
            <p className="text-base font-bold text-content-1000">
              Canadian criminal background check email was sent
            </p>
          </Show>
          <Show if={activityItem.activity_type === "note"}>
            <p className="text-base font-bold text-content-1000">
              {activityItem.author?.name}{" "}
              <span className="font-normal">left a note</span>
            </p>
          </Show>

          {activityItem.activity_type === "invited" ? (
            <p className="text-base font-bold text-content-1000">
              {(activityItem as ApplicationActivity).details?.email}{" "}
              <span className="font-normal">
                invited as{" "}
                {(activityItem as ApplicationActivity).details?.invitee_role ===
                ApplicantType.CoSigner
                  ? "a "
                  : "an "}
                {
                  ApplicantRoleLabel[
                    (activityItem as ApplicationActivity).details
                      ?.invitee_role || ""
                  ]
                }
              </span>
            </p>
          ) : null}

          <Show if={activityItem.activity_type === "resent_invite"}>
            <div className="flex flex-col gap-1">
              <p className="text-base font-bold text-content-1000">
                Invitation was resent
              </p>
              <p className="text-sm font-normal text-content-400">
                {(activityItem as ApplicationActivity).details?.email} (
                {(activityItem as ApplicationActivity).details?.invitee_role ===
                ApplicantType.CoSigner
                  ? "a "
                  : "an "}
                <span className="lowercase">
                  {
                    ApplicantRoleLabel[
                      (activityItem as ApplicationActivity).details
                        ?.invitee_role || ""
                    ]
                  }
                </span>
                ) was emailed an invitation
              </p>
            </div>
          </Show>
          <Show if={activityItem.activity_type === "group_submitted"}>
            <p className="text-base font-bold text-content-1000">
              Application is pending a decision
            </p>
          </Show>
          <Show if={activityItem.activity_type === "decision_email_sent"}>
            <p className="text-base font-bold text-content-1000">
              Decision email was sent
            </p>
          </Show>
          <Show if={activityItem.activity_type === "credit_report_sent"}>
            <p className="text-base font-bold text-content-1000">
              Credit report was automatically emailed
            </p>
          </Show>
          <Show if={activityItem.activity_type === "submitted"}>
            <p className="text-base font-bold text-content-1000">
              {activityItem.author?.name} ({activityItem.author?.email}){" "}
              <span className="font-normal">submitted their application</span>
            </p>
          </Show>
          <Show if={activityItem.activity_type === "decision_made"}>
            <p className="text-base font-bold text-content-1000">
              {
                applicationDecisionStatus[
                  (activityItem as ApplicationActivity).details?.status || ""
                ]
              }
            </p>
          </Show>
          <Show if={activityItem.activity_type === "auto_approved"}>
            <p className="text-base font-bold text-content-1000">
              Application automatically approved
            </p>
          </Show>
          <Show if={activityItem.activity_type === "note"}>
            <Popover className="absolute -right-4 -top-2">
              <Popover.Button>
                <IconButton
                  variant={IconButtonVariant.BASE}
                  size={IconButtonSize.SM}
                  borderLess
                  className="bg-transparent hover:bg-alpha-40"
                  Icon={DotsHorizontal}
                />
              </Popover.Button>
              <Popover.Panel className="absolute right-0 z-10 mt-2 w-[140px] rounded-lg border border-alpha-100 bg-background-1000 shadow-xl">
                <Popover.Button className="w-full">
                  <Button
                    size={ButtonSize.SM}
                    variant={ButtonVariant.TEXT}
                    onClick={() => setEditMode(true)}
                    className={buttonClassName}
                  >
                    Edit note
                  </Button>
                </Popover.Button>
                <Popover.Button className="w-full">
                  <Button
                    size={ButtonSize.SM}
                    variant={ButtonVariant.TEXT}
                    onClick={() =>
                      deleteNoteMeta.mutate(
                        Number((activityItem as ApplicationNote).note_id)
                      )
                    }
                    className={`${buttonClassName} border-t border-alpha-40 !text-danger-500`}
                  >
                    Delete note
                  </Button>
                </Popover.Button>
              </Popover.Panel>
            </Popover>
          </Show>
        </div>

        <Show if={typeof activityItem.details === "string"}>
          {editMode && activityItem.activity_type === "note" ? (
            <>
              <TextArea
                id="note"
                placeholder="You can write a note here"
                value={newMessage}
                rows={4}
                onChange={(e) => setNewMessage(e.target.value)}
              />
              <div className="mt-2 flex justify-end gap-2">
                <Button
                  variant={ButtonVariant.BASE}
                  size={ButtonSize.SM}
                  onClick={() => setEditMode(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant={ButtonVariant.PRIMARY}
                  size={ButtonSize.SM}
                  onClick={() => {
                    editNoteMeta.mutate({
                      noteId: Number((activityItem as ApplicationNote).note_id),
                      newMessage
                    });
                    setEditMode(false);
                  }}
                >
                  Save
                </Button>
              </div>
            </>
          ) : (
            <div>
              <p className="text-sm text-content-1000">
                {activityItem.details as string}
              </p>

              <Show if={activityItem.updated_at}>
                <div className="flex border-t border-alpha-40 gap-2 items-center mt-4 pt-4">
                  <Pencil02 className="stroke-content-200" size="20" />
                  <p className="text-content-400 font-normal text-sm">
                    Edited at{" "}
                    {moment(activityItem.updated_at).format(
                      "MMM DD, YYYY [at] h:mm A"
                    )}
                  </p>
                </div>
              </Show>
            </div>
          )}
        </Show>

        <Show if={activityItem.activity_type === "auto_approved"}>
          <p className="text-sm font-normal text-content-400">by Boom</p>
        </Show>

        {activityItem.activity_type === "remove_criminal_report" ? (
          <>
            <span className="text-base font-bold text-content-1000">
              Criminal record has been removed by Boom Admin&nbsp;
            </span>
            {activityItem.details?.contact ? (
              <span className="text-base text-content-1000">
                for&nbsp;
                {getFullName({
                  firstName: activityItem.details.contact.first_name || "",
                  middleName: activityItem.details.contact.middle_name || "",
                  lastName: activityItem.details.contact.last_name || ""
                })}
                &nbsp;
              </span>
            ) : null}
            <span className="text-base text-content-1000 capitalize">
              (
              <ApplicantRole
                role={activityItem.details?.applicant_role as ApplicantType}
              />
              )
            </span>
            <p className="mt-2 text-sm font-normal text-content-400">
              Boom has removed&nbsp;
              {activityItem.details?.offense_description || ""}
              &nbsp;from the criminal report. This typically occurs when there
              is a dispute and Boom has sufficient evidence that the record
              should not be surfaced on the report.
            </p>
          </>
        ) : null}
        {activityItem.activity_type === "canceled" ||
        activityItem.activity_type === "uncanceled" ? (
          <>
            {activityItem.details?.auto_canceled ? (
              <span className="text-base font-bold text-content-1000">
                Application was automatically {activityItem.activity_type}
              </span>
            ) : (
              <>
                <span className="text-base font-bold text-content-1000">
                  Application was {activityItem.activity_type} for{" "}
                </span>
                {activityItem.details?.contact?.first_name
                  ? getFullName({
                      firstName: activityItem.details.contact.first_name || "",
                      lastName: activityItem.details.contact.last_name || "",
                      middleName: activityItem.details.contact.middle_name
                    })
                  : "everyone in group"}
              </>
            )}
          </>
        ) : null}

        {activityItem.details ? (
          <>
            <Show if={activityItem.activity_type === "group_submitted"}>
              <p className="text-sm font-normal text-content-400">
                All applicants have submitted their rental applications
              </p>
            </Show>

            <Show if={message}>
              <p className="text-sm font-normal text-content-400">{message}</p>
            </Show>

            <Show if={activityItem.activity_type === "decision_made"}>
              {getAuthorText({
                authorName: activityItem.author?.name,
                authorRole: activityItem.author?.author_role
              })}
            </Show>

            {["canceled", "uncanceled"].includes(activityItem.activity_type) &&
            activityItem.author?.name ? (
              <p className="text-sm font-normal text-content-400">
                by {activityItem.author.name} (
                {RoleTitles[activityItem.author.author_role] || "--"})
              </p>
            ) : null}
          </>
        ) : null}
      </div>

      <p className="flex justify-end mt-2 text-sm text-content-300 mr-4">
        {moment(activityItem.created_at).format("MMM DD, YYYY [at] h:mm A")}
      </p>
    </div>
  );
};
